import React, { useState, useEffect } from 'react'
import './StockImages.css'

/**
 * 무료 스톡 이미지 검색 컴포넌트
 */
function StockImages() {
  const [searchQuery, setSearchQuery] = useState('')
  const [stockImages, setStockImages] = useState([])
  const [savedImages, setSavedImages] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [lastSearchQuery, setLastSearchQuery] = useState('')
  const [isUsingCache, setIsUsingCache] = useState(false)
  const [activeView, setActiveView] = useState('search') // 'search' or 'saved'

  // 캐시 설정
  const CACHE_DURATION = 24 * 60 * 60 * 1000 // 24시간
  const CACHE_KEY_PREFIX = 'pexels_search_'

  // 컴포넌트 마운트시 캐시 복원
  useEffect(() => {
    // 마지막 검색 결과 캐시에서 복원
    const lastSearch = localStorage.getItem('last_search_query')
    if (lastSearch) {
      setSearchQuery(lastSearch)
      loadFromCacheOrAPI(lastSearch)
    } else {
      loadCuratedImages()
    }

    // 저장된 이미지 목록 복원
    const saved = localStorage.getItem('saved_stock_images')
    if (saved) {
      try {
        setSavedImages(JSON.parse(saved))
      } catch (error) {
        console.error('저장된 이미지 복원 오류:', error)
      }
    }
  }, [])

  // 캐시에서 로드하거나 API 호출
  const loadFromCacheOrAPI = async (query) => {
    const cacheKey = `${CACHE_KEY_PREFIX}${query}`
    const cached = localStorage.getItem(cacheKey)
    
    if (cached) {
      try {
        const cachedData = JSON.parse(cached)
        if (Date.now() - cachedData.timestamp < CACHE_DURATION) {
          setStockImages(cachedData.images)
          setIsUsingCache(true)
          setLastSearchQuery(query)
          console.log('캐시에서 로드됨:', query)
          return
        }
      } catch (error) {
        console.error('캐시 파싱 오류:', error)
      }
    }
    
    await searchFromAPI(query)
  }

  // API에서 검색
  const searchFromAPI = async (query) => {
    setIsSearching(true)
    setIsUsingCache(false)
    
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
      const response = await fetch(
        `${API_BASE_URL}/api/pexels/search?query=${encodeURIComponent(query)}&page=1&per_page=30`
      )
      
      if (!response.ok) {
        throw new Error(`검색 실패: ${response.status}`)
      }
      
      const data = await response.json()
      
      const formattedResults = data.photos.map(photo => ({
        id: `pexels-${photo.id}`,
        url: photo.src.large,
        thumbnail: photo.src.medium,
        description: photo.alt || query,
        author: photo.photographer,
        source: 'pexels',
        downloadUrl: photo.src.original,
        photographerUrl: photo.photographer_url,
        pexelsUrl: photo.url
      }))
      
      // 캐시에 저장
      const cacheKey = `${CACHE_KEY_PREFIX}${query}`
      localStorage.setItem(cacheKey, JSON.stringify({
        images: formattedResults,
        timestamp: Date.now(),
        query: query
      }))
      
      localStorage.setItem('last_search_query', query)
      
      setStockImages(formattedResults)
      setLastSearchQuery(query)
      console.log('API에서 새로 로드됨:', query)
    } catch (error) {
      console.error('Stock search failed:', error)
      alert('이미지 검색에 실패했습니다: ' + error.message)
    } finally {
      setIsSearching(false)
    }
  }

  // 인기 이미지 로드
  const loadCuratedImages = async () => {
    const cacheKey = `${CACHE_KEY_PREFIX}_curated`
    const cached = localStorage.getItem(cacheKey)
    
    if (cached) {
      try {
        const cachedData = JSON.parse(cached)
        if (Date.now() - cachedData.timestamp < CACHE_DURATION) {
          setStockImages(cachedData.images)
          setIsUsingCache(true)
          console.log('인기 이미지 캐시에서 로드됨')
          return
        }
      } catch (error) {
        console.error('캐시 파싱 오류:', error)
      }
    }
    
    setIsSearching(true)
    setIsUsingCache(false)
    
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
      const response = await fetch(`${API_BASE_URL}/api/pexels/curated?page=1&per_page=30`)
      
      if (!response.ok) {
        throw new Error(`Failed to load curated images: ${response.status}`)
      }
      
      const data = await response.json()
      
      const formattedResults = data.photos.map(photo => ({
        id: `pexels-${photo.id}`,
        url: photo.src.large,
        thumbnail: photo.src.medium,
        description: photo.alt || '인기 이미지',
        author: photo.photographer,
        source: 'pexels',
        downloadUrl: photo.src.original,
        photographerUrl: photo.photographer_url,
        pexelsUrl: photo.url
      }))
      
      localStorage.setItem(cacheKey, JSON.stringify({
        images: formattedResults,
        timestamp: Date.now()
      }))
      
      setStockImages(formattedResults)
      console.log('인기 이미지 API에서 로드됨')
    } catch (error) {
      console.error('Failed to load curated images:', error)
    } finally {
      setIsSearching(false)
    }
  }

  // 검색 처리
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      alert('검색어를 입력해주세요')
      return
    }
    await loadFromCacheOrAPI(searchQuery)
  }

  // 이미지 저장
  const saveImage = (image) => {
    const exists = savedImages.find(img => img.id === image.id)
    if (!exists) {
      const newSaved = [image, ...savedImages]
      setSavedImages(newSaved)
      localStorage.setItem('saved_stock_images', JSON.stringify(newSaved))
      alert('이미지가 저장되었습니다')
    } else {
      alert('이미 저장된 이미지입니다')
    }
  }

  // 저장된 이미지 삭제
  const removeImage = (imageId) => {
    const newSaved = savedImages.filter(img => img.id !== imageId)
    setSavedImages(newSaved)
    localStorage.setItem('saved_stock_images', JSON.stringify(newSaved))
  }

  // 다운로드 추적
  const trackDownload = async (image) => {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
      await fetch(`${API_BASE_URL}/api/pexels/track-download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          photoId: image.id.replace('pexels-', ''),
          photoUrl: image.downloadUrl,
          photographer: image.author
        })
      })
    } catch (error) {
      console.error('Download tracking failed:', error)
    }
  }

  return (
    <div className="stock-images-container">
      {/* 헤더 */}
      <div className="stock-header">
        <h2>무료 스톡 이미지</h2>
        <div className="view-switcher">
          <button 
            className={`view-btn ${activeView === 'search' ? 'active' : ''}`}
            onClick={() => setActiveView('search')}
          >
            🔍 검색
          </button>
          <button 
            className={`view-btn ${activeView === 'saved' ? 'active' : ''}`}
            onClick={() => setActiveView('saved')}
          >
            💾 저장됨 ({savedImages.length})
          </button>
        </div>
      </div>

      <div className="stock-content">
        {activeView === 'search' ? (
          <>
            {/* 검색 바 */}
            <div className="search-section">
              <div className="search-bar">
                <input
                  type="text"
                  className="search-input"
                  placeholder="이미지 검색... (예: nature, business, technology)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <button 
                  className="search-btn"
                  onClick={handleSearch}
                  disabled={isSearching}
                >
                  {isSearching ? '검색 중...' : '검색'}
                </button>
              </div>

              {/* 인기 검색어 */}
              <div className="popular-tags">
                <span>인기:</span>
                {['nature', 'business', 'technology', 'food', 'travel', 'people', 'abstract'].map(tag => (
                  <button 
                    key={tag}
                    className="tag-btn"
                    onClick={() => {
                      setSearchQuery(tag)
                      loadFromCacheOrAPI(tag)
                    }}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* 검색 결과 */}
            <div className="results-section">
              {lastSearchQuery && (
                <div className="results-header">
                  <h3>
                    {lastSearchQuery ? `"${lastSearchQuery}"` : '인기 이미지'}
                    {isUsingCache && <span className="cache-badge">캐시</span>}
                  </h3>
                  <span className="results-count">{stockImages.length}개 결과</span>
                </div>
              )}

              {stockImages.length === 0 && !isSearching ? (
                <div className="no-results">
                  <p>검색 결과가 없습니다</p>
                  <p className="hint">키워드를 입력하여 무료 이미지를 검색해보세요!</p>
                </div>
              ) : (
                <div className="images-grid">
                  {stockImages.map(image => (
                    <div key={image.id} className="stock-image-card">
                      <img src={image.thumbnail} alt={image.description} loading="lazy" />
                      <div className="image-overlay">
                        <div className="image-info">
                          <p className="image-desc">{image.description}</p>
                          <p className="image-author">by {image.author}</p>
                        </div>
                        <div className="image-actions">
                          <button 
                            className="action-btn save"
                            onClick={() => saveImage(image)}
                            title="저장"
                          >
                            💾
                          </button>
                          <a 
                            href={image.downloadUrl}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            className="action-btn download"
                            onClick={() => trackDownload(image)}
                            title="다운로드"
                          >
                            ⬇️
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          /* 저장된 이미지 뷰 */
          <div className="saved-section">
            {savedImages.length === 0 ? (
              <div className="no-saved">
                <p>저장된 이미지가 없습니다</p>
                <p className="hint">검색 결과에서 💾 버튼을 눌러 이미지를 저장하세요</p>
              </div>
            ) : (
              <div className="images-grid">
                {savedImages.map(image => (
                  <div key={image.id} className="stock-image-card">
                    <img src={image.thumbnail} alt={image.description} loading="lazy" />
                    <div className="image-overlay">
                      <div className="image-info">
                        <p className="image-desc">{image.description}</p>
                        <p className="image-author">by {image.author}</p>
                      </div>
                      <div className="image-actions">
                        <button 
                          className="action-btn remove"
                          onClick={() => removeImage(image.id)}
                          title="삭제"
                        >
                          🗑️
                        </button>
                        <a 
                          href={image.downloadUrl}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          className="action-btn download"
                          onClick={() => trackDownload(image)}
                          title="다운로드"
                        >
                          ⬇️
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default StockImages